import type { HTMLChakraProps } from '@chakra-ui/react';
import type { HTMLMotionProps } from 'framer-motion';

/**
 * `containerProps` value for Chakra `DrawerContent` that marks the drawer's
 * outer fullscreen wrapper so global CSS can scope rules to side drawers
 * only (e.g. `pointer-events: none` to let the LHS page stay clickable
 * without affecting other Chakra modals).
 *
 * `data-*` attributes are used (instead of `className`) because Chakra's
 * `DrawerContent` overrides `className` after spreading `containerProps`.
 * The cast is required because Chakra's `HTMLChakraProps<'div'>` typing
 * does not enumerate arbitrary `data-` attributes.
 */
export const SIDE_DRAWER_CONTAINER_PROPS = {
    'data-partbay-side-drawer': 'true',
} as unknown as HTMLChakraProps<'div'>;

/**
 * `motionProps` for a right-placed Chakra `DrawerContent` that makes the
 * open/close instant. Chakra's `Drawer` has no `motionPreset`/duration prop, so
 * we override the internal `Slide` variants (which otherwise carry the default
 * 0.2s enter / 0.1s exit transition) with zero-duration equivalents. The enter/
 * exit transforms mirror Chakra's `slideRight` variant so positioning is intact.
 */
export const INSTANT_SIDE_DRAWER_MOTION_PROPS: HTMLMotionProps<'div'> = {
    variants: {
        enter: { x: 0, y: 0, transition: { duration: 0 } },
        exit: { x: '100%', y: 0, transition: { duration: 0 } },
    },
};

/**
 * `motionProps` for Chakra `DrawerOverlay`/`ModalOverlay` that makes the dim
 * appear/disappear instantly. Mirrors Chakra's `fadeConfig` with zero-duration
 * variants, so it pairs with `INSTANT_SIDE_DRAWER_MOTION_PROPS` when one drawer
 * swaps for another without the overlay flickering.
 */
export const INSTANT_DRAWER_OVERLAY_MOTION_PROPS: HTMLMotionProps<'div'> = {
    initial: 'exit',
    animate: 'enter',
    exit: 'exit',
    variants: {
        enter: { opacity: 1, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
    },
};
